import { useState } from 'react'
import { Link, useLocation } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard,
  Monitor,
  BarChart3,
  Volume2,
  FileSearch,
  FileText,
  Settings,
  ChevronLeft,
  ChevronRight,
  Globe,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { supportedLanguages } from '@/i18n/config'

const navItems = [
  { key: 'nav.dashboard', icon: LayoutDashboard, path: '/dashboard' },
  {
    key: 'nav.monitor',
    icon: Monitor,
    path: '/monitor',
    children: [
      { key: 'nav.monitorManage', path: '/monitor' },
      { key: 'nav.monitorData', path: '/monitor/data' },
    ],
  },
  { key: 'nav.analysis', icon: BarChart3, path: '/analysis' },
  { key: 'nav.volume', icon: Volume2, path: '/volume' },
  { key: 'nav.publicOption', icon: FileSearch, path: '/publicoption' },
  { key: 'nav.report', icon: FileText, path: '/report' },
  {
    key: 'nav.settings',
    icon: Settings,
    path: '/settings/warning',
    children: [
      { key: 'nav.settingsWarning', path: '/settings/warning' },
      { key: 'nav.settingsWarningMsg', path: '/settings/warning-msg' },
      { key: 'nav.settingsPreference', path: '/settings/preference' },
      { key: 'nav.settingsUsers', path: '/settings/users' },
      { key: 'nav.settingsFeedback', path: '/settings/feedback' },
    ],
  },
]

export function Sidebar() {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState<string[]>([])

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    )
  }

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang)
    localStorage.setItem('lang', lang)
  }

  return (
    <aside
      className={cn(
        'flex h-screen flex-col border-r bg-card transition-all duration-300',
        collapsed ? 'w-16' : 'w-60'
      )}
    >
      {/* Logo */}
      <div className="flex h-16 items-center justify-between border-b px-4">
        {!collapsed && (
          <span className="text-lg font-bold truncate bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
            {t('common.appName')}
          </span>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCollapsed(!collapsed)}
          className="ml-auto shrink-0"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        <ul className="space-y-1 px-2">
          {navItems.map((item) => {
            const Icon = item.icon
            const hasChildren = item.children && item.children.length > 0
            const isExpanded = expandedGroups.includes(item.key)
            const isActive = location.pathname.startsWith(item.path)

            return (
              <li key={item.key}>
                {hasChildren ? (
                  <>
                    <button
                      onClick={() => toggleGroup(item.key)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-accent',
                        isActive && 'bg-primary/10 text-primary',
                        collapsed && 'justify-center'
                      )}
                    >
                      <Icon className="h-5 w-5 shrink-0" />
                      {!collapsed && (
                        <>
                          <span className="flex-1 text-left">{t(item.key)}</span>
                          <ChevronRight
                            className={cn(
                              'h-4 w-4 transition-transform',
                              isExpanded && 'rotate-90'
                            )}
                          />
                        </>
                      )}
                    </button>
                    {!collapsed && isExpanded && (
                      <ul className="ml-6 mt-1 space-y-1 border-l pl-3">
                        {item.children!.map((child) => (
                          <li key={child.key}>
                            <Link
                              to={child.path}
                              className={cn(
                                'block rounded-lg px-3 py-1.5 text-sm transition-colors hover:bg-accent',
                                location.pathname === child.path &&
                                  'bg-primary/10 text-primary font-medium'
                              )}
                            >
                              {t(child.key)}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.path}
                    className={cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-accent',
                      isActive && 'bg-primary/10 text-primary',
                      collapsed && 'justify-center'
                    )}
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    {!collapsed && <span>{t(item.key)}</span>}
                  </Link>
                )}
              </li>
            )
          })}
        </ul>
      </nav>

      {/* Language Selector */}
      <div className="border-t p-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="w-full justify-start gap-2">
              <Globe className="h-4 w-4 shrink-0" />
              {!collapsed && (
                <span className="truncate">
                  {supportedLanguages.find((l) => l.code === i18n.language)?.name || 'Language'}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" className="w-48">
            {supportedLanguages.map((lang) => (
              <DropdownMenuItem
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className={cn(
                  'cursor-pointer',
                  i18n.language === lang.code && 'bg-primary/10 text-primary'
                )}
              >
                <span className="mr-2">{lang.flag}</span>
                {lang.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  )
}
